import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Settings, Sparkles, Film, Users, Image, Wand2, Download, Plus, MoreVertical, SlidersHorizontal, LayoutGrid, Zap, AlignLeft, Brush } from 'lucide-react';
import { useTheme } from '../../../../core/contexts/ThemeContext';
interface VideoStudioAppProps {
  onBack: () => void;
}
export type ActiveStudioPanel = 'concept' | 'synthesis' | 'polish';
export type ActiveSidebar = 'characters' | 'properties' | null;
export function VideoStudioApp({
  onBack
}: VideoStudioAppProps) {
  const {
    getThemeClasses,
    theme: currentTheme
  } = useTheme();
  const theme = getThemeClasses();
  const [activePanel, setActivePanel] = useState<ActiveStudioPanel>('concept');
  const [selectedCharacter, setSelectedCharacter] = useState<number | null>(1);
  const [selectedScene, setSelectedScene] = useState<number | null>(1);
  const [activeSidebar, setActiveSidebar] = useState<ActiveSidebar>(null);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const panels = useMemo(() => [{
    id: 'concept' as const,
    label: 'Concept',
    icon: Sparkles,
    color: 'from-purple-500 to-violet-500',
    subtitle: 'Blueprint & Story Outline'
  }, {
    id: 'synthesis' as const,
    label: 'Synthesis',
    icon: Wand2,
    color: 'from-fuchsia-500 to-pink-500',
    subtitle: 'AI Generation & Refinement'
  }, {
    id: 'polish' as const,
    label: 'polish' as const,
    icon: Film,
    color: 'from-rose-500 to-orange-500',
    subtitle: 'Edits, Filters & Export'
  }], []);
  const characters = useMemo(() => [{
    id: 1,
    name: 'Cyan',
    role: 'Protagonist',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aUWGZFgSmKF83Z6Ju9xhwY/Whisk_e1f8ba05d957e2c8ed34cf640dad53d1eg.png',
    style: 'Vibrant Cyberpunk'
  }, {
    id: 2,
    name: 'Violet',
    role: 'Antagonist',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qtB8P8sGePdjsd5NJS1X98/Whisk_b9efbba9dfbe503b42844c1e812bee38dr.jpg',
    style: 'Mysterious Gothic'
  }, {
    id: 3,
    name: 'Duo',
    role: 'Team',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    style: 'Dynamic Contrast'
  }], []);
  const scenes = useMemo(() => [{
    id: 1,
    title: 'Scene 1',
    duration: '5s',
    status: 'generated',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aUWGZFgSmKF83Z6Ju9xhwY/Whisk_e1f8ba05d957e2c8ed34cf640dad53d1eg.png',
    description: 'Cyan character introduction in modern interior.'
  }, {
    id: 2,
    title: 'Scene 2',
    duration: '8s',
    status: 'generated',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/stCMFT6LkkVykASQVNXRp6/aac4bfad-b894-401d-92ac-ad2a80272b62_%281%29.png',
    description: 'Confrontation between Cyan and Violet.',
    videoUrl: 'https://drive.google.com/file/d/14ZHULrGyJQkKQKuUvvja7mO2PQ8pq4y4/preview'
  }, {
    id: 3,
    title: 'Scene 3',
    duration: '7s',
    status: 'generated',
    thumbnail: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qtB8P8sGePdjsd5NJS1X98/Whisk_b9efbba9dfbe503b42844c1e812bee38dr.jpg',
    description: 'Violet in atmospheric gothic setting.'
  }], []);
  const selectedSceneData = scenes.find(s => s.id === selectedScene);
  const activePanelData = panels.find(p => p.id === activePanel);
  // Component voor Character Sidebar (Links)
  const CharacterSidebar = () => <div className={`lg:block w-64 backdrop-blur-xl border-r p-4 space-y-4 overflow-y-auto ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-white border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/30' : 'bg-black/20 border-white/10'}`} data-id="element-619">
      <div data-id="element-620">
        <div className="flex items-center justify-between mb-3" data-id="element-621">
          <div className={`text-xs font-bold uppercase ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/40'}`} data-id="element-622">
            Characters
          </div>
          <button className={`p-1 rounded transition-colors ${isLightTheme ? 'hover:bg-gray-200' : isHighContrast ? 'hover:bg-gray-100' : isNeonTheme ? 'hover:bg-fuchsia-900/30' : 'hover:bg-white/10'}`} data-id="element-623">
            <Plus className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-624" />
          </button>
        </div>
        <div className="space-y-2" data-id="element-625">
          {characters.map(char => <button key={char.id} onClick={() => setSelectedCharacter(char.id)} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all text-left ${selectedCharacter === char.id ? isLightTheme ? 'bg-rose-50 border-rose-200 border' : isHighContrast ? 'bg-gray-200 border-2 border-black' : isNeonTheme ? 'bg-rose-900/20 border border-rose-500/50' : 'bg-rose-500/10 border border-rose-500/30' : isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : isHighContrast ? 'bg-white hover:bg-gray-100 border border-black' : isNeonTheme ? 'bg-fuchsia-900/20 hover:bg-fuchsia-900/30' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-626">
              <img src={char.image} alt={char.name} className="w-10 h-10 rounded-lg" data-id="element-627" />
              <div className="flex-1" data-id="element-628">
                <div className={`text-sm font-medium ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-629">
                  {char.name}
                </div>
                <div className={`text-xs ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/40'}`} data-id="element-630">
                  {char.role}
                </div>
              </div>
            </button>)}
        </div>
      </div>
      <div className="pt-4 border-t border-white/10" data-id="element-631">
        <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-632">
          Tools
        </div>
        <button className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors ${isLightTheme ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100' : 'bg-white/10 text-white/80 hover:bg-white/20'}`} data-id="element-633">
          <Users className="w-4 h-4" data-id="element-634" />
          Manage Roles
        </button>
      </div>
    </div>;
  // Component voor Properties Sidebar (Rechts)
  const PropertiesSidebar = () => <div className={`xl:block w-80 backdrop-blur-xl border-l p-4 space-y-4 overflow-y-auto ${isLightTheme ? 'bg-gray-50 border-gray-200' : isHighContrast ? 'bg-white border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/30' : 'bg-black/20 border-white/10'}`} data-id="element-635">
      <motion.div key={activePanel + '-props'} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="space-y-4" data-id="element-636">
        <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-637">
          {activePanelData?.label} Properties
        </div>

        {/* CONCEPT Panel Content */}
        {activePanel === 'concept' && <div className="space-y-4" data-id="element-638">
            <div data-id="element-639">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-640">
                Story Prompt
              </label>
              <textarea rows={4} defaultValue="Epic cinematic short film featuring Hero and Mentor, set in a dystopian future." className={`w-full rounded-lg px-3 py-2 text-sm transition-colors resize-none ${isLightTheme ? 'bg-white border border-gray-300 text-gray-900 focus:border-purple-500' : 'bg-white/5 border border-white/10 text-white focus:border-fuchsia-500 focus:outline-none'}`} data-id="element-641" />
            </div>
            <div data-id="element-642">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-643">
                Visual Style Preset
              </label>
              <select className={`w-full rounded-lg px-3 py-2 text-sm transition-colors ${isLightTheme ? 'bg-white border border-gray-300 text-gray-900 focus:border-purple-500' : 'bg-white/5 border border-white/10 text-white focus:border-fuchsia-500 focus:outline-none'}`} data-id="element-644">
                <option data-id="element-645">Cinematic Unreal Engine</option>
                <option data-id="element-646">Stylized Anime</option>
                <option data-id="element-647">Abstract Watercolor</option>
              </select>
            </div>
            <button className="w-full py-3 rounded-lg bg-gradient-to-r from-purple-500 to-violet-500 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform text-sm shadow-md shadow-purple-500/30" data-id="element-648">
              <Sparkles className="w-4 h-4" data-id="element-649" />
              Generate Blueprint
            </button>
          </div>}

        {/* SYNTHESIS Panel Content */}
        {activePanel === 'synthesis' && <div className="space-y-4" data-id="element-650">
            <p className={`text-sm ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-651">
              **Synthesis** is the AI refinement stage. Fine-tune visual details
              and motion *before* final rendering.
            </p>
            <div data-id="element-652">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-653">
                Refinement Strength
              </label>
              <input type="range" className="w-full accent-fuchsia-500" min="0" max="100" defaultValue="70" data-id="element-654" />
              <div className={`text-xs mt-1 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-655">
                High Detail (70%)
              </div>
            </div>
            <div data-id="element-656">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-657">
                Motion Style
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-658">
                {['Dolly Zoom', 'Slow Pan', 'Static Shot'].map(style => <button key={style} className={`py-2 rounded-lg text-xs transition-colors ${isLightTheme ? 'bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10 text-white/80'}`} data-id="element-659">
                    {style}
                  </button>)}
              </div>
            </div>
            <button className="w-full py-3 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform text-sm shadow-md shadow-fuchsia-500/30" data-id="element-660">
              <Wand2 className="w-4 h-4" data-id="element-661" />
              Run Synthesis Iteration
            </button>
          </div>}

        {/* POLISH Panel Content */}
        {activePanel === 'polish' && <div className="space-y-4" data-id="element-662">
            <p className={`text-sm ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-663">
              **Polish** is the final stage for color grading, sound design, and
              export settings.
            </p>
            <div data-id="element-664">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-665">
                Color Grading LUT
              </label>
              <select className={`w-full rounded-lg px-3 py-2 text-sm transition-colors ${isLightTheme ? 'bg-white border border-gray-300 text-gray-900 focus:border-purple-500' : 'bg-white/5 border border-white/10 text-white focus:border-fuchsia-500 focus:outline-none'}`} data-id="element-666">
                <option data-id="element-667">Cyberpunk Neon</option>
                <option data-id="element-668">Classic Noir</option>
                <option data-id="element-669">Vibrant Cinema</option>
              </select>
            </div>
            <div data-id="element-670">
              <label className={`text-xs block mb-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-671">
                Aspect Ratio
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-672">
                {['16:9', '9:16', '1:1'].map(ratio => <button key={ratio} className={`py-2 rounded-lg text-xs transition-colors ${isLightTheme ? 'bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10 text-white/80'}`} data-id="element-673">
                    {ratio}
                  </button>)}
              </div>
            </div>
            <div className="pt-4 border-t border-white/10" data-id="element-674">
              <div className={`text-xs font-bold uppercase mb-3 ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-675">
                Export
              </div>
              <button className="w-full py-3 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 text-white font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform text-sm shadow-md shadow-rose-500/30" data-id="element-676">
                <Download className="w-4 h-4" data-id="element-677" />
                Export Video
              </button>
            </div>
          </div>}
      </motion.div>
    </div>;
  // --- MAIN RENDER ---
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.98
  }} className={`h-full flex flex-col ${theme.bg}`} data-id="element-678">
      {/* Toolbar - Header */}
      <div className={`flex items-center justify-between px-3 md:px-6 py-2.5 md:py-4 z-20 ${theme.card} ${isLightTheme ? 'border-gray-200' : 'border-white/10'} border-b backdrop-blur-sm`} data-id="element-679">
        {/* Left */}
        <div className="flex items-center gap-2 md:gap-4" data-id="element-680">
          <button onClick={onBack} className={`flex items-center gap-1 md:gap-2 transition-colors touch-manipulation p-1.5 rounded-lg ${isLightTheme ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-white/60 hover:text-white hover:bg-white/5'}`} aria-label="Back to Launcher" data-id="element-681">
            <ArrowLeft className="w-4 h-4" data-id="element-682" />
            <span className="hidden sm:inline text-sm" data-id="element-683">Back</span>
          </button>
          <div className={`hidden sm:block w-px h-6 ${isLightTheme ? 'bg-gray-200' : 'bg-white/10'}`} data-id="element-684" />
          <div className="flex items-center gap-2" data-id="element-685">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-pink-500/30" data-id="element-686">
              <Film className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" data-id="element-687" />
            </div>
            <div data-id="element-688">
              <div className={`text-xs md:text-sm font-bold ${theme.text}`} data-id="element-689">
                AI Video Studio
              </div>
              <div className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-690">
                Untitled Project
              </div>
            </div>
          </div>
        </div>

        {/* Center - Phase Selector (Desktop) */}
        <div className={`hidden md:flex items-center gap-1 p-1 rounded-xl shadow-inner ${isLightTheme ? 'bg-gray-100' : 'bg-white/5'}`} data-id="element-691">
          {panels.map(panel => {
          const Icon = panel.icon;
          const isActive = activePanel === panel.id;
          return <button key={panel.id} onClick={() => setActivePanel(panel.id)} className={`relative px-3 md:px-4 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-all touch-manipulation ${isActive ? 'text-white' : isLightTheme ? 'text-gray-600 hover:text-gray-900' : 'text-white/50 hover:text-white/80'}`} data-id="element-692">
                {isActive && <motion.div layoutId="activePanel" className={`absolute inset-0 bg-gradient-to-r ${panel.color} rounded-lg shadow-md`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-693" />}
                <div className="relative z-10 flex items-center gap-2" data-id="element-694">
                  <Icon className="w-3 h-3 md:w-4 md:h-4" data-id="element-695" />
                  <span data-id="element-696">{panel.label}</span>
                </div>
              </button>;
        })}
        </div>

        {/* Right - Actions */}
        <div className="flex items-center gap-1.5 md:gap-2" data-id="element-697">
          {/* Mobile Sidebar Toggles */}
          <button onClick={() => setActiveSidebar('characters')} className={`md:hidden p-1.5 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10'}`} aria-label="Toggle Characters" data-id="element-698">
            <Users className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-699" />
          </button>
          <button onClick={() => setActiveSidebar('properties')} className={`md:hidden p-1.5 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10'}`} aria-label="Toggle Properties" data-id="element-700">
            <SlidersHorizontal className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-701" />
          </button>
          <div className={`hidden md:block w-px h-6 ${isLightTheme ? 'bg-gray-200' : 'bg-white/10'}`} data-id="element-702" />

          <button className={`p-1.5 md:p-2 rounded-lg transition-colors touch-manipulation ${isLightTheme ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-703">
            <Settings className={`w-4 h-4 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-704" />
          </button>
          <button className="px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg bg-gradient-to-r from-rose-500 to-pink-600 text-white font-medium flex items-center gap-1.5 md:gap-2 hover:scale-[1.05] transition-transform text-xs md:text-sm touch-manipulation shadow-md shadow-pink-500/40" aria-label="Run AI" data-id="element-705">
            <Zap className="w-3 h-3 md:w-4 md:h-4" data-id="element-706" />
            <span className="hidden sm:inline" data-id="element-707">Run AI</span>
          </button>
        </div>
      </div>

      {/* Mobile Phase Selector */}
      <div className={`md:hidden flex items-center gap-2 px-3 py-2.5 border-b overflow-x-auto z-10 ${isLightTheme ? 'bg-gray-100 border-gray-200' : 'bg-black/20 border-white/10'}`} data-id="element-708">
        {panels.map(panel => {
        const Icon = panel.icon;
        const isActive = activePanel === panel.id;
        return <button key={panel.id} onClick={() => setActivePanel(panel.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all touch-manipulation ${isActive ? `bg-gradient-to-r ${panel.color} text-white shadow-sm` : isLightTheme ? 'bg-white border border-gray-300 text-gray-700' : 'bg-white/5 text-white/50 hover:bg-white/10'}`} data-id="element-709">
              <Icon className="w-3.5 h-3.5" data-id="element-710" />
              {panel.label}
            </button>;
      })}
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative" data-id="element-711">
        {/* Left Sidebar - Characters */}
        <div className="hidden lg:block" data-id="element-712">
          <CharacterSidebar data-id="element-713" />
        </div>

        {/* Center Canvas & Workflow View */}
        <div className="flex-1 flex flex-col min-w-0" data-id="element-714">
          {/* Workflow View */}
          <div className="flex-1 p-3 md:p-8 overflow-y-auto" data-id="element-715">
            <div className="max-w-5xl mx-auto" data-id="element-716">
              <AnimatePresence mode="wait" data-id="element-717">
                <motion.div key={activePanel + '-workflow'} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} transition={{
                duration: 0.3
              }} className="flex flex-col gap-6" data-id="element-718">
                  {/* Phase Header & Description (FIXED) */}
                  <div className="mb-2 w-full" data-id="element-719">
                    <h2 className={`text-xl md:text-3xl font-bold ${activePanelData?.color.split(' ').slice(-2).join(' ')} bg-clip-text text-transparent`} data-id="element-720">
                      {activePanelData?.label}{' '}
                      <span className={`text-base font-medium ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-721">
                        {activePanelData?.subtitle}
                      </span>
                    </h2>
                    <p className={`text-sm mt-1 ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`} data-id="element-722">
                      {activePanel === 'concept' && 'Defineer het verhaal, de karakters en de visuele stijl. Dit resulteert in een gedetailleerde blueprint.'}
                      {activePanel === 'synthesis' && 'Verfijn de AI-gegenereerde beelden, pas de cameralogica aan en itereer op de prompts om de gewenste look te bereiken.'}
                      {activePanel === 'polish' && 'Voeg de laatste handelingen toe: kleurcorrectie, sound design synchronisatie en kies de exportformaten.'}
                    </p>
                  </div>

                  {/* Actuele Canvas Inhoud per Fase - Concept FIX */}
                  {activePanel === 'concept' && <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl border p-4 md:p-6 shadow-xl flex flex-col md:flex-row gap-6 ${isLightTheme ? 'bg-white border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-723">
                      <div className="flex-1 space-y-4" data-id="element-724">
                        <h3 className={`text-lg font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-725">
                          AI Storyboard (Blueprint)
                        </h3>
                        <div className={`aspect-video rounded-lg md:rounded-xl flex items-center justify-center relative overflow-hidden ${isLightTheme ? 'bg-gray-100' : 'bg-black/50'}`} data-id="element-726">
                          <AlignLeft className={`w-8 h-8 md:w-12 md:h-12 absolute ${isLightTheme ? 'text-gray-400' : 'text-white/30'}`} data-id="element-727" />
                          <div className="absolute inset-0 p-4 text-xs overflow-y-auto" data-id="element-728">
                            <p className={`${isLightTheme ? 'text-gray-700' : 'text-white/70'}`} data-id="element-729">
                              **SCENE 1: INTRODUCTION.** Setting: Neon-drenched
                              futuristic city street. Action: Hero (realistic
                              style) steps out of a vintage car. Mood:
                              Mysterious. Duration: 5s.
                            </p>
                            <p className={`mt-2 ${isLightTheme ? 'text-gray-700' : 'text-white/70'}`} data-id="element-730">
                              **SCENE 2: CONFRONTATION.** Setting: Industrial
                              rooftop at night. Action: Mentor appears, giving
                              cryptic advice. Mood: Tense. Duration: 8s.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="w-full md:w-64 space-y-4" data-id="element-731">
                        <h3 className={`text-lg font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-732">
                          Character View
                        </h3>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/10" data-id="element-733">
                          <img src={characters.find(c => c.id === selectedCharacter)?.image} alt="Character" className="w-12 h-12 rounded-full border-2 border-purple-500" data-id="element-734" />
                          <div data-id="element-735">
                            <div className={`font-medium ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-736">
                              {characters.find(c => c.id === selectedCharacter)?.name}
                            </div>
                            <div className={`text-xs ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-737">
                              Selected:{' '}
                              {characters.find(c => c.id === selectedCharacter)?.style}
                            </div>
                          </div>
                        </div>
                        <button className="w-full py-2.5 rounded-lg bg-white/10 text-white/80 text-sm hover:bg-white/20 transition-colors" data-id="element-738">
                          Generate Character Sheet
                        </button>
                      </div>
                    </div>}

                  {/* 2. SYNTHESIS: Focus op Preview & Prompts */}
                  {activePanel === 'synthesis' && <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl border p-4 md:p-6 shadow-xl ${isLightTheme ? 'bg-white border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-739">
                      <div className="flex flex-col md:flex-row gap-6" data-id="element-740">
                        <div className="flex-1" data-id="element-741">
                          <h3 className={`text-lg font-bold mb-3 ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-742">
                            Scene Preview ({selectedSceneData?.title})
                          </h3>
                          <div className={`aspect-video rounded-lg md:rounded-xl flex items-center justify-center relative group overflow-hidden ${isLightTheme ? 'bg-gray-100' : 'bg-black/50'}`} data-id="element-743">
                            {selectedSceneData?.videoUrl ? <iframe src={selectedSceneData.videoUrl} className="w-full h-full" allow="autoplay" title={selectedSceneData.title} data-id="element-744" /> : <>
                                <img src={selectedSceneData?.thumbnail} alt={selectedSceneData?.title} className="w-full h-full object-cover" data-id="element-745" />
                                <Play className="absolute w-10 h-10 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-746" />
                              </>}
                          </div>
                        </div>
                        <div className="w-full md:w-80 space-y-4" data-id="element-747">
                          <h3 className={`text-lg font-bold ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-748">
                            Refinement Prompt
                          </h3>
                          <textarea rows={4} defaultValue="Add more volumetric lighting and sharp reflections on the vehicle." className={`w-full rounded-lg px-3 py-2 text-sm transition-colors resize-none ${isLightTheme ? 'bg-gray-100 border border-gray-300 text-gray-900 focus:border-fuchsia-500' : 'bg-white/5 border border-white/10 text-white focus:border-fuchsia-500 focus:outline-none'}`} data-id="element-749" />
                          <button className="w-full py-3 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform text-sm shadow-md shadow-fuchsia-500/30" data-id="element-750">
                            <Wand2 className="w-4 h-4" data-id="element-751" />
                            Synthesize Scene
                          </button>
                        </div>
                      </div>
                    </div>}

                  {/* 3. POLISH: Focus op Timeline & Final Output */}
                  {activePanel === 'polish' && <div className={`backdrop-blur-xl rounded-xl md:rounded-2xl border p-4 md:p-6 shadow-xl ${isLightTheme ? 'bg-white border-gray-200' : 'bg-white/5 border-white/10'}`} data-id="element-752">
                      <h3 className={`text-lg font-bold mb-4 ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-753">
                        Final Render Preview
                      </h3>
                      <div className={`aspect-video rounded-lg md:rounded-xl flex items-center justify-center relative overflow-hidden ${isLightTheme ? 'bg-gray-100' : 'bg-black/50'}`} data-id="element-754">
                        <Brush className={`w-8 h-8 md:w-12 md:h-12 absolute ${isLightTheme ? 'text-gray-400' : 'text-white/30'}`} data-id="element-755" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4" data-id="element-756">
                          <p className="text-white font-medium" data-id="element-757">
                            Final Video Ready for Export
                          </p>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-between items-center border-t border-white/10 pt-4" data-id="element-758">
                        <div className="space-y-1" data-id="element-759">
                          <p className={`text-sm font-medium ${isLightTheme ? 'text-gray-900' : 'text-white'}`} data-id="element-760">
                            Total Duration: 20 seconds
                          </p>
                          <p className={`text-xs ${isLightTheme ? 'text-gray-600' : 'text-white/40'}`} data-id="element-761">
                            Resolution: 4K (3840x2160)
                          </p>
                        </div>
                        <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-orange-500 text-white font-medium flex items-center gap-2 hover:scale-105 transition-transform text-sm shadow-md shadow-rose-500/30" data-id="element-762">
                          <Download className="w-4 h-4" data-id="element-763" />
                          Export Video
                        </button>
                      </div>
                    </div>}

                  {/* Scene Grid (Onderste, kleinere previews) */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-764">
                    {scenes.map(scene => <button key={scene.id} onClick={() => setSelectedScene(scene.id)} className={`relative group rounded-xl overflow-hidden border transition-all ${selectedScene === scene.id ? isLightTheme ? 'border-rose-500 ring-2 ring-rose-200' : isHighContrast ? 'border-black ring-4 ring-gray-300' : 'border-rose-500/50 ring-2 ring-rose-500/20' : isLightTheme ? 'border-gray-200 hover:border-gray-300' : 'border-white/10 hover:border-white/20'}`} data-id="element-765">
                        <div className="aspect-video relative" data-id="element-766">
                          <img src={scene.thumbnail} alt={scene.title} className="w-full h-full object-cover" data-id="element-767" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-768" />
                          <div className="absolute bottom-2 left-2 right-2" data-id="element-769">
                            <div className="text-white text-xs font-bold mb-1" data-id="element-770">
                              {scene.title}
                            </div>
                            <div className="flex items-center justify-between" data-id="element-771">
                              <div className="text-white/60 text-xs" data-id="element-772">
                                {scene.duration}
                              </div>
                              <div className={`w-2 h-2 rounded-full ${scene.status === 'generated' ? 'bg-emerald-500' : scene.status === 'generating' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-500'}`} data-id="element-773" />
                            </div>
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Timeline - Desktop (Onder) */}
          <div className={`hidden md:block h-32 backdrop-blur-xl border-t p-4 z-10 ${isLightTheme ? 'bg-gray-50 border-gray-200' : 'bg-black/40 border-white/10'}`} data-id="element-774">
            <div className="flex items-center justify-between mb-2" data-id="element-775">
              <div className={`text-xs font-bold uppercase ${isLightTheme ? 'text-gray-500' : 'text-white/40'}`} data-id="element-776">
                Timeline
              </div>
              <button className={`text-xs transition-colors p-1 rounded ${isLightTheme ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-id="element-777">
                <Plus className="w-3 h-3 inline mr-1" data-id="element-778" /> Add Scene
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-779">
              {scenes.map(scene => <div key={scene.id} onClick={() => setSelectedScene(scene.id)} className={`w-24 h-16 flex-shrink-0 rounded-lg border flex flex-col items-center justify-center text-xs cursor-pointer transition-all p-1.5 ${selectedScene === scene.id ? isLightTheme ? 'bg-rose-50 border-rose-500' : 'bg-rose-500/10 border-rose-500/50' : isLightTheme ? 'bg-gray-100 border-gray-200 hover:border-gray-300' : 'bg-white/5 border-white/10 hover:border-white/30'}`} data-id="element-780">
                  <div className={`font-medium ${isLightTheme ? 'text-gray-900' : 'text-white/80'}`} data-id="element-781">
                    {scene.title}
                  </div>
                  <div className={`mt-1 ${isLightTheme ? 'text-gray-500' : 'text-white/50'}`} data-id="element-782">
                    {scene.duration}
                  </div>
                </div>)}
              <button className={`w-24 h-16 flex-shrink-0 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${isLightTheme ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50' : 'border-white/20 hover:border-white/40 hover:bg-white/5'}`} data-id="element-783">
                <Plus className={`w-5 h-5 ${isLightTheme ? 'text-gray-400' : 'text-white/40'}`} data-id="element-784" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties */}
        <div className="hidden xl:block" data-id="element-785">
          <PropertiesSidebar data-id="element-786" />
        </div>

        {/* Mobile Sidebar Overlay */}
        <AnimatePresence data-id="element-787">
          {activeSidebar && <motion.div initial={{
          opacity: 0,
          x: '100%'
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: '100%'
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-30 md:hidden" data-id="element-788">
              <div className="absolute inset-0 bg-black/50" onClick={() => setActiveSidebar(null)} data-id="element-789" />
              <div className="absolute top-0 bottom-0 right-0 w-64" data-id="element-790">
                {activeSidebar === 'characters' ? <CharacterSidebar data-id="element-791" /> : <PropertiesSidebar data-id="element-792" />}
                <button onClick={() => setActiveSidebar(null)} className="absolute top-4 right-4 text-white/80 hover:text-white" aria-label="Close Sidebar" data-id="element-793">
                  <ArrowLeft className="w-6 h-6 rotate-90" data-id="element-794" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}